import { RadioButton } from "primereact/radiobutton";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { selectedPayment } from "../../redux/slices/payments";
import AuthService from "../../services/autentication/auth.service";
import OrderService from "../../services/order/order.service";
import './sales-ordering.css';

const SalesOrderingPaymentMode = ({ paymentmode, isSelfPickup, deliveryMethod, selectedUserId }: any) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const selectedPaymentMode = useAppSelector((state) => state.payments);
    const [selectedPaymentId, setSelectedPaymentId] = useState<number>(0);
    const isAdmin = useAppSelector((state) => state.isAdmin);
    const courier_charges = localStorage.getItem('courier-charges');
    const [isExecutive, setIsExecutive] = useState<boolean>(false);
    const [isCardAndUpiAllowed, setIsCardAndUpiAllowed] = useState<boolean>(false);

    // Same as payment-mode.tsx
    const GetUserRoleId = () => {
        if (AuthService.isUserLoggedIn()) {
            const localData = localStorage.getItem("user");
            const parsedData = JSON.parse(localData !== null ? localData : "");
            return parsedData.roleID === 4 || parsedData.roleID === 8 ? true : false;
        } else {
            return false;
        }
    };

    const checkCardAndUpiEligibility = async () => {
        try {
            // First check the logged-in user's swipe machine assignment
            let res = await OrderService.checkCardAndUpiEligibility();

            // If the logged-in user doesn't have a machine, check if the selected customer has one
            if (!res.isSuccess && selectedUserId) {
                res = await OrderService.checkCardAndUpiEligibility(Number(selectedUserId));
            }

            setIsCardAndUpiAllowed(res.isSuccess);
        } catch (error) {
            console.error("Error checking Card and UPI eligibility:", error);
            setIsCardAndUpiAllowed(false);
        }
    };

    let paymentmodesdata: any[];

    // This component is specifically for Sales Ordering, so we should always show 
    // the "internal staff" payment modes (like Cash, UPI, Card), only filtering 
    // Card/UPI based on machine availability mapping.
    if (courier_charges === '0') {
        paymentmodesdata = [
            {
                id: "11",
                mode: "Cash At Store",
                code: "CASH",
                modeImage: process.env.PUBLIC_URL + "/assests/images/cash.jpg",
                isSelected: false,
            },
            {
                id: "1",
                mode: t("payment_mode1"),
                code: "COD",
                modeImage: process.env.PUBLIC_URL + "/assests/images/cod.jpg",
                isSelected: true,
            },
            {
                id: "6",
                mode: "Payment Link",
                code: "",
                modeImage: process.env.PUBLIC_URL + "/assests/images/Betaallink.png",
                isSelected: true,
            },
            {
                id: "7",
                mode: "Online Payment",
                code: "OPTONL",
                modeImage: process.env.PUBLIC_URL + "/assests/images/credit-card.png",
                isSelected: false,
            },
        ];
        if (isCardAndUpiAllowed) {
            paymentmodesdata.push(
                {
                    id: "5",
                    mode: "UPI",
                    code: "OPTUPI",
                    modeImage: process.env.PUBLIC_URL + "/assests/images/oup.jpg",
                    isSelected: true,
                },
                {
                    id: "9",
                    mode: "Card",
                    code: "OPTONL",
                    modeImage: process.env.PUBLIC_URL + "/assests/images/credit-card.png",
                    isSelected: false,
                }
            );
        }
    } else {
        paymentmodesdata = [
            {
                id: "11",
                mode: "Cash At Store",
                code: "CASH",
                modeImage: process.env.PUBLIC_URL + "/assests/images/cash.jpg",
                isSelected: false,
            },
            {
                id: "1",
                mode: t("payment_mode1"),
                code: "COD",
                modeImage: process.env.PUBLIC_URL + "/assests/images/cod.jpg",
                isSelected: true,
            },
            {
                id: "7",
                mode: "Online Payment",
                code: "OPTONL",
                modeImage: process.env.PUBLIC_URL + "/assests/images/credit-card.png",
                isSelected: true,
            },
            {
                id: "6",
                mode: "Payment Link",
                code: "",
                modeImage: process.env.PUBLIC_URL + "/assests/images/Betaallink.png",
                isSelected: true,
            },
        ];
        if (isCardAndUpiAllowed) {
            paymentmodesdata.push(
                {
                    id: "5",
                    mode: "UPI",
                    code: "OPTUPI",
                    modeImage: process.env.PUBLIC_URL + "/assests/images/oup.jpg",
                    isSelected: true,
                },
                {
                    id: "9",
                    mode: "Card",
                    code: "OPTONL",
                    modeImage: process.env.PUBLIC_URL + "/assests/images/credit-card.png",
                    isSelected: false,
                }
            );
        }
    }

    // Filter out COD if not Home Delivery
    if (isSelfPickup || (deliveryMethod && deliveryMethod !== 'HOME_DELIVERY')) {
        paymentmodesdata = paymentmodesdata.filter((p: any) => p.code !== 'COD');
    }

    // Keep selected payment in sync with Redux (same as payment-mode.tsx)
    useEffect(() => {
        if (selectedPaymentMode.selectedPayment === null) {
            setSelectedPaymentId(0);
        } else {
            setSelectedPaymentId(selectedPaymentMode.selectedPayment.id);
        }
    });

    // On mount: set isExecutive, clear selection, and call CheckUserDeviceMapping (same as payment-mode.tsx)
    useEffect(() => {
        dispatch(selectedPayment(null));
        setSelectedPaymentId(0);
        setIsExecutive(GetUserRoleId());
        checkCardAndUpiEligibility(); // → calls api/Order/CheckUserDeviceMapping
    }, [selectedUserId]);

    // Clear selection if the current selected payment is no longer valid (e.g., COD hidden)
    useEffect(() => {
        if (selectedPaymentId !== 0) {
            const isCurrentlyAvailable = paymentmodesdata.some(p => p.id === selectedPaymentId.toString());
            if (!isCurrentlyAvailable) {
                setSelectedPaymentId(0);
                dispatch(selectedPayment(null));
                paymentmode(null);
            }
        }
    }, [isSelfPickup, deliveryMethod]);

    const savePaymentMode = (e: any, value: any) => {
        paymentmode(value);
        setSelectedPaymentId(value.id);
        dispatch(selectedPayment(value));
    };

    return (
        <div className="grid">
            {paymentmodesdata.map((category) => {
                const isSelected = selectedPaymentId.toString() === category.id;
                return (
                    <div key={category.id} className="col-12 sm:col-6">
                        <div
                            className={`p-3 border-round-xl cursor-pointer transition-all duration-200 flex align-items-center justify-content-between`}
                            onClick={(e) => savePaymentMode(e, category)}
                            style={{
                                border: isSelected ? '2px solid #10b981' : '1px solid #e2e8f0',
                                backgroundColor: isSelected ? '#ecfdf5' : '#ffffff',
                                boxShadow: isSelected ? '0 4px 12px rgba(16, 185, 129, 0.15)' : '0 2px 6px rgba(0,0,0,0.04)',
                                transform: isSelected ? 'translateY(-2px)' : 'none'
                            }}
                        >
                            <div className="flex align-items-center gap-3">
                                <div
                                    className="flex align-items-center justify-content-center border-circle"
                                    style={{
                                        width: '24px', height: '24px',
                                        border: isSelected ? 'none' : '2px solid #cbd5e1',
                                        backgroundColor: isSelected ? '#10b981' : 'transparent',
                                        color: '#fff'
                                    }}
                                >
                                    {isSelected && <i className="pi pi-check text-sm font-bold" />}
                                </div>
                                <span className="font-semibold" style={{ color: isSelected ? '#065f46' : '#334155', fontSize: '0.95rem' }}>
                                    {category.mode}
                                </span>
                            </div>
                            <img
                                src={category.modeImage}
                                alt={category.mode}
                                style={{ width: "42px", height: "32px", objectFit: "contain", borderRadius: "4px", backgroundColor: '#fff', padding: '2px', border: '1px solid #f1f5f9' }}
                                onError={(e: any) => { e.target.style.display = "none"; }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default SalesOrderingPaymentMode;
